/**
 * For the node being edited: every place a value can come from, with the
 * fields each one offers. Upstream nodes first (nearest first), then the
 * workflow's variables and the built-ins.
 *
 * A node's fields come from the last run where there was one, and from a
 * declared shape otherwise: a connector action's output schema, a webhook's
 * request envelope, a callable's inputs. Nodes that pass items through — a
 * filter, a sort, an if — inherit their predecessor's shape, and `Set fields`
 * adds its own on top.
 */

import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { connectorsApi } from '@/core/api/connectors.api'
import { useConnections } from '@/core/connection/connection.context'
import type { JsonSchema } from '@/core/types/api.types'
import type { NodeSpec, StepRecord, WfEdge, WfNode } from '../types'
import { fieldsFromSample, fieldsFromSchema, itemSchema, mergeFields, typeOf, type FieldNode } from './schema'
import { contractFields, parseSchema } from './WebhookContract'

export interface Source {
  key: string
  title: string
  subtitle: string
  /** Where an expression for this source starts: `$json`, `$node("X").first`, `$vars`… */
  root: string
  /** `$json` means this node — the immediate predecessor. */
  direct: boolean
  node?: WfNode
  fields: FieldNode[]
  fromRun: boolean
  loading?: boolean
}

const PASS_THROUGH = new Set([
  'flow.if', 'flow.switch', 'flow.merge', 'flow.wait', 'data.filter', 'data.sort', 'data.limit', 'data.dedupe', 'output.log', 'output.respond',
])

export function useSources(
  node: WfNode,
  nodes: WfNode[],
  edges: WfEdge[],
  specs: Record<string, NodeSpec>,
  steps: Record<string, StepRecord>,
  variables: Record<string, unknown>,
  insideLoop: boolean,
): Source[] {
  const { byId: connectionsById } = useConnections()

  const { direct, all, parentsOf } = useMemo(() => upstream(node.id, nodes, edges), [node.id, nodes, edges])

  // Declared output shapes come from action manifests; one query per distinct action upstream.
  const wanted = useMemo(() => {
    const seen = new Map<string, { connectorId: string; actionId: string }>()
    for (const n of all) {
      if (n.type !== 'connector.action' && n.type !== 'trigger.app') continue
      const actionId = String(n.params.action_id ?? '')
      const connectorId = String(n.params.connector_id ?? connectionsById[String(n.params.connection_id ?? '')]?.connector_id ?? '')
      if (actionId && connectorId) seen.set(`${connectorId}/${actionId}`, { connectorId, actionId })
    }
    return [...seen.entries()]
  }, [all, connectionsById])

  const schemaQueries = useQueries({
    queries: wanted.map(([key, w]) => ({
      queryKey: ['workflows', 'action-schema', w.connectorId, w.actionId],
      queryFn: () => connectorsApi.actionSchema(w.connectorId, w.actionId),
      staleTime: 10 * 60_000,
      meta: { key },
    })),
  })
  const stamp = schemaQueries.map((q) => `${q.dataUpdatedAt}:${q.isLoading ? 1 : 0}`).join(',')
  const { schemas, loading } = useMemo(() => {
    const out: Record<string, JsonSchema | undefined> = {}
    let busy = false
    wanted.forEach(([key], i) => {
      out[key] = schemaQueries[i]?.data?.output_schema
      if (schemaQueries[i]?.isLoading) busy = true
    })
    return { schemas: out, loading: busy }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, stamp])

  return useMemo(() => {
    const directIds = new Set(direct.map((n) => n.id))
    const declaredCache = new Map<string, FieldNode[]>()

    function declared(n: WfNode, depth = 0): FieldNode[] {
      if (depth > 12) return []
      const hit = declaredCache.get(n.id)
      if (hit) return hit
      let out: FieldNode[] = []
      const sample = sampleOf(n)
      switch (n.type) {
        case 'connector.action':
        case 'trigger.app': {
          const actionId = String(n.params.action_id ?? '')
          const connectorId = String(n.params.connector_id ?? connectionsById[String(n.params.connection_id ?? '')]?.connector_id ?? '')
          const schema = schemas[`${connectorId}/${actionId}`]
          out = fieldsFromSchema(itemSchema(schema))
          if (n.type === 'trigger.app' && sample !== undefined) out = mergeFields(out, fieldsFromSample(sample))
          break
        }
        case 'trigger.callable':
          out = ((n.params.inputs as { key?: string; value?: unknown }[]) ?? [])
            .filter((f) => f.key)
            .map((f) => ({ path: String(f.key), key: String(f.key), type: typeOf(f.value), sample: f.value }))
          if (sample && typeof sample === 'object') out = mergeFields(out, fieldsFromSample(sample))
          break
        case 'trigger.webhook': {
          const env = sample && typeof sample === 'object' && !Array.isArray(sample) ? (sample as Record<string, unknown>) : {}
          const declared = (raw: unknown, base: string): FieldNode[] =>
            contractFields(raw).filter((f) => f.name).map((f) => ({ path: `${base}.${f.name}`, key: f.name, type: f.type, description: `${f.required ? 'required' : 'optional'}${f.description ? ' · ' + f.description : ''}` }))
          const pathParams = [...String(n.params.path ?? '').matchAll(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g)].map((m) => m[1])
          const schema = parseSchema(n.params.body_schema)
          const bodyDeclared = schema ? fieldsFromSchema(itemSchema(schema as JsonSchema), 'body') : []
          out = [
            { path: 'method', key: 'method', type: 'string', sample: env.method ?? (n.params.method === 'ANY' ? 'POST' : n.params.method) },
            { path: 'path', key: 'path', type: 'string', sample: env.path ?? n.params.path },
            { path: 'params', key: 'params', type: 'object', children: mergeFields(pathParams.map((k) => ({ path: `params.${k}`, key: k, type: 'string' })), fieldsFromSample(env.params ?? {}, 'params')) },
            { path: 'query', key: 'query', type: 'object', sample: env.query, children: mergeFields(declared(n.params.query, 'query'), fieldsFromSample(env.query ?? {}, 'query')) },
            { path: 'headers', key: 'headers', type: 'object', sample: env.headers, children: mergeFields(declared(n.params.headers, 'headers'), fieldsFromSample(env.headers ?? {}, 'headers')) },
            { path: 'body', key: 'body', type: typeOf(env.body ?? {}), sample: env.body, children: mergeFields(bodyDeclared, fieldsFromSample(env.body ?? {}, 'body')) },
          ]
          break
        }
        case 'trigger.manual':
        case 'trigger.schedule':
          out = sample !== undefined ? fieldsFromSample(Array.isArray(sample) ? sample[0] : sample) : n.type === 'trigger.schedule' ? [{ path: 'scheduled_at', key: 'scheduled_at', type: 'string' }] : []
          break
        case 'http.request':
          out = n.params.body_only
            ? []
            : [
                { path: 'status', key: 'status', type: 'number' },
                { path: 'headers', key: 'headers', type: 'object' },
                { path: 'body', key: 'body', type: 'any' },
              ]
          break
        case 'data.aggregate': {
          const key = String(n.params.key || 'items')
          const inner = parentsOf(n.id)[0]
          out = [
            { path: key, key, type: 'array', children: inner ? [{ path: `${key}.0`, key: '[0]', type: 'object', children: declared(inner, depth + 1) }] : [] },
            { path: 'count', key: 'count', type: 'number' },
          ]
          break
        }
        case 'data.set': {
          const inner = parentsOf(n.id)[0]
          const base = n.params.keep_existing === false || !inner ? [] : declared(inner, depth + 1)
          const added = ((n.params.fields as { key?: string; value?: unknown }[]) ?? [])
            .filter((f) => f.key)
            .map((f) => ({ path: String(f.key), key: String(f.key), type: typeof f.value === 'string' && f.value.includes('{{') ? 'any' : typeOf(f.value), sample: typeof f.value === 'string' && f.value.includes('{{') ? undefined : f.value }))
          out = mergeFields(base.filter((b) => !added.some((a) => a.key === b.key)), added)
          break
        }
        case 'data.split': {
          const inner = parentsOf(n.id)[0]
          const path = String(n.params.path ?? '')
          const parent = inner ? declared(inner, depth + 1) : []
          const found = path ? findPath(parent, `${path}.0`) : undefined
          out = found?.children ?? []
          break
        }
        case 'brain.search':
          out = [
            { path: 'kind', key: 'kind', type: 'string', description: 'record or chunk' },
            { path: 'ref_id', key: 'ref_id', type: 'string', description: 'The record or chunk id' },
            { path: 'title', key: 'title', type: 'string' },
            { path: 'text', key: 'text', type: 'string' },
            { path: 'score', key: 'score', type: 'number' },
            { path: 'node_type_key', key: 'node_type_key', type: 'string' },
          ]
          break
        case 'brain.ask':
          out = [
            { path: 'answer', key: 'answer', type: 'string' },
            { path: 'citations', key: 'citations', type: 'array', children: [{ path: 'citations.0', key: '[0]', type: 'object' }] },
          ]
          break
        case 'brain.ingest':
          out = [
            { path: 'received', key: 'received', type: 'number' },
            { path: 'staged', key: 'staged', type: 'number' },
            { path: 'duplicates', key: 'duplicates', type: 'number' },
            { path: 'source_id', key: 'source_id', type: 'string' },
          ]
          break
        case 'brain.define_type':
          out = [
            { path: 'node_type_id', key: 'node_type_id', type: 'string' },
            { path: 'key', key: 'key', type: 'string' },
            { path: 'display_name', key: 'display_name', type: 'string' },
            { path: 'created', key: 'created', type: 'boolean' },
            { path: 'added_properties', key: 'added_properties', type: 'array' },
          ]
          break
        case 'brain.define_edge_type':
          out = [
            { path: 'edge_type_id', key: 'edge_type_id', type: 'string' },
            { path: 'key', key: 'key', type: 'string' },
            { path: 'from_node_type', key: 'from_node_type', type: 'string' },
            { path: 'to_node_type', key: 'to_node_type', type: 'string' },
            { path: 'created', key: 'created', type: 'boolean' },
          ]
          break
        case 'brain.link':
          out = [
            { path: 'edge_id', key: 'edge_id', type: 'string' },
            { path: 'edge_type_key', key: 'edge_type_key', type: 'string' },
            { path: 'from_entity', key: 'from_entity', type: 'string' },
            { path: 'to_entity', key: 'to_entity', type: 'string' },
            { path: 'attributes', key: 'attributes', type: 'object' },
          ]
          break
        case 'brain.graph':
          out = [
            { path: 'graph_id', key: 'graph_id', type: 'string' },
            { path: 'name', key: 'name', type: 'string' },
            { path: 'description', key: 'description', type: 'string' },
            { path: 'status', key: 'status', type: 'string' },
            { path: 'created', key: 'created', type: 'boolean' },
            ...(n.params.action === 'clone' ? [{ path: 'cloned_from', key: 'cloned_from', type: 'string' }, { path: 'copied', key: 'copied', type: 'object' }] : []),
            ...(n.params.action === 'delete' ? [{ path: 'deleted', key: 'deleted', type: 'boolean' }] : []),
          ]
          break
        case 'brain.dq_rule':
          out = [{ path: 'rule_id', key: 'rule_id', type: 'string' }, { path: 'key', key: 'key', type: 'string' }, { path: 'name', key: 'name', type: 'string' }, { path: 'kind', key: 'kind', type: 'string' }, { path: 'severity', key: 'severity', type: 'string' }, { path: 'property_key', key: 'property_key', type: 'string' }, { path: 'definition', key: 'definition', type: 'object' }, { path: 'active', key: 'active', type: 'boolean' }, { path: 'created', key: 'created', type: 'boolean' }]
          break
        case 'brain.match_rule':
          out = [{ path: 'match_rule_id', key: 'match_rule_id', type: 'string' }, { path: 'key', key: 'key', type: 'string' }, { path: 'name', key: 'name', type: 'string' }, { path: 'fields', key: 'fields', type: 'array' }, { path: 'action', key: 'action', type: 'string' }, { path: 'auto_merge_threshold', key: 'auto_merge_threshold', type: 'number' }, { path: 'review_threshold', key: 'review_threshold', type: 'number' }, { path: 'active', key: 'active', type: 'boolean' }, { path: 'created', key: 'created', type: 'boolean' }]
          break
        case 'brain.survivorship_rule':
          out = [{ path: 'survivorship_rule_id', key: 'survivorship_rule_id', type: 'string' }, { path: 'property_key', key: 'property_key', type: 'string' }, { path: 'strategy', key: 'strategy', type: 'string' }, { path: 'source_priority', key: 'source_priority', type: 'array' }, { path: 'active', key: 'active', type: 'boolean' }, { path: 'created', key: 'created', type: 'boolean' }]
          break
        case 'brain.source':
          out = [{ path: 'id', key: 'id', type: 'string' }, { path: 'source_id', key: 'source_id', type: 'string' }, { path: 'connection_id', key: 'connection_id', type: 'string' }, { path: 'connector_id', key: 'connector_id', type: 'string' }, { path: 'display_name', key: 'display_name', type: 'string' }, { path: 'kind', key: 'kind', type: 'string' }, { path: 'mode', key: 'mode', type: 'string' }, { path: 'created', key: 'created', type: 'boolean' }]
          break
        case 'brain.records':
        case 'brain.record':
        case 'brain.upsert_record': {
          const record: FieldNode[] = [
            { path: 'entity_id', key: 'entity_id', type: 'string', description: 'The record id — what links and lookups use' },
            { path: 'node_type_key', key: 'node_type_key', type: 'string' },
            { path: 'label', key: 'label', type: 'string' },
            { path: 'properties', key: 'properties', type: 'object', description: 'The record\'s fields' },
            { path: 'status', key: 'status', type: 'string' },
            ...(n.type === 'brain.upsert_record' ? [{ path: 'created', key: 'created', type: 'boolean' }] : []),
            ...(n.type === 'brain.record' ? [{ path: 'edges', key: 'edges', type: 'array' }, { path: 'sources', key: 'sources', type: 'array' }] : []),
          ]
          const inner = parentsOf(n.id)[0]
          if (n.params.keep_input && inner) {
            // The items carried on; the record rides along as `result`.
            out = mergeFields(declared(inner, depth + 1), [{ path: 'result', key: 'result', type: 'object', children: record }])
          } else out = record
          break
        }
        default: {
          if (PASS_THROUGH.has(n.type) || n.type === 'flow.for_each' || n.type === 'flow.while') {
            const inner = parentsOf(n.id)[0]
            out = inner ? declared(inner, depth + 1) : []
          }
        }
      }
      declaredCache.set(n.id, out)
      return out
    }

    const sources: Source[] = all.map((n) => {
      const step = steps[n.id]
      const port = step ? mainPort(step) : null
      const first = port ? step!.output[port]?.[0] : undefined
      const fromRun = first !== undefined && first !== null && typeof first === 'object'
      const runFields = fromRun ? fieldsFromSample(first) : []
      const fields = fromRun ? mergeFields(declared(n), runFields) : declared(n)
      const spec = specs[n.type]
      const isDirect = directIds.has(n.id)
      return {
        key: n.id,
        title: n.name,
        subtitle: isDirect ? 'previous step · $json' : spec?.label ?? n.type,
        root: isDirect ? '$json' : `$node(${JSON.stringify(n.name)}).first`,
        direct: isDirect,
        node: n,
        fields,
        fromRun,
        loading: loading && (n.type === 'connector.action' || n.type === 'trigger.app') && !fromRun,
      }
    })

    const varFields = Object.entries(variables).map(([k, v]) => ({ path: k, key: k, type: typeOf(v), sample: v }))
    sources.push({ key: '$vars', title: 'Variables', subtitle: '$vars · set on the workflow', root: '$vars', direct: false, fields: varFields, fromRun: false })
    if (insideLoop) {
      sources.push({
        key: '$loop',
        title: 'Loop',
        subtitle: '$loop · this iteration',
        root: '$loop',
        direct: false,
        fields: [
          { path: 'index', key: 'index', type: 'number', description: '0-based iteration' },
          { path: 'count', key: 'count', type: 'number', description: 'Total iterations, when known' },
          { path: 'item', key: 'item', type: 'any', description: 'The item this pass received' },
        ],
        fromRun: false,
      })
    }
    sources.push({
      key: '$builtins',
      title: 'Run',
      subtitle: 'ids and time',
      root: '',
      direct: false,
      fields: [
        { path: '$now', key: '$now', type: 'string', description: 'The current time, ISO 8601' },
        { path: '$run.id', key: '$run.id', type: 'string' },
        { path: '$run.workflow_id', key: '$run.workflow_id', type: 'string' },
        { path: '$index', key: '$index', type: 'number', description: 'Position of the current item' },
        { path: 'len($items)', key: 'len($items)', type: 'number', description: 'How many items reached this node' },
      ],
      fromRun: false,
    })
    return sources
  }, [all, direct, parentsOf, steps, specs, variables, insideLoop, schemas, loading, connectionsById])
}

// --- helpers --------------------------------------------------------------------------------

function upstream(nodeId: string, nodes: WfNode[], edges: WfEdge[]) {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const parents = new Map<string, string[]>()
  for (const e of edges) parents.set(e.target, [...(parents.get(e.target) ?? []), e.source])
  const parentsOf = (id: string): WfNode[] => (parents.get(id) ?? []).map((p) => byId.get(p)).filter((n): n is WfNode => Boolean(n))
  const direct = parentsOf(nodeId)
  const all: WfNode[] = []
  const seen = new Set<string>([nodeId])
  const queue = [...(parents.get(nodeId) ?? [])]
  while (queue.length) {
    const id = queue.shift()!
    if (seen.has(id)) continue
    seen.add(id)
    const n = byId.get(id)
    if (!n) continue
    all.push(n)
    queue.push(...(parents.get(id) ?? []))
  }
  return { direct, all, parentsOf }
}

function sampleOf(n: WfNode): unknown {
  const raw = n.params.sample
  if (typeof raw === 'string') {
    if (!raw.trim()) return undefined
    try {
      return JSON.parse(raw)
    } catch {
      return raw
    }
  }
  return raw ?? undefined
}

function mainPort(step: StepRecord): string | null {
  const ports = Object.keys(step.output)
  if (!ports.length) return null
  if (ports.includes('main')) return 'main'
  if (ports.includes('done')) return 'done'
  return ports.find((p) => (step.output[p]?.length ?? 0) > 0) ?? ports[0]
}

function findPath(fields: FieldNode[], path: string): FieldNode | undefined {
  for (const f of fields) {
    if (f.path === path) return f
    if (path.startsWith(`${f.path}.`) && f.children) {
      const hit = findPath(f.children, path)
      if (hit) return hit
    }
  }
  return undefined
}

/** Whether a node sits inside a loop body — reachable from a loop port and not from done. */
export function isInsideLoop(nodeId: string, nodes: WfNode[], edges: WfEdge[], specs: Record<string, NodeSpec>): boolean {
  const out = new Map<string, { port: string; target: string }[]>()
  for (const e of edges) out.set(e.source, [...(out.get(e.source) ?? []), { port: e.source_port, target: e.target }])
  const reach = (starts: string[]) => {
    const seen = new Set<string>()
    const stack = [...starts]
    while (stack.length) {
      const id = stack.pop()!
      if (seen.has(id)) continue
      seen.add(id)
      for (const e of out.get(id) ?? []) stack.push(e.target)
    }
    return seen
  }
  for (const n of nodes) {
    const lp = specs[n.type]?.loop_ports
    if (!lp) continue
    const fromLoop = reach((out.get(n.id) ?? []).filter((e) => e.port === lp[0]).map((e) => e.target))
    const fromDone = reach((out.get(n.id) ?? []).filter((e) => e.port === lp[1]).map((e) => e.target))
    if (fromLoop.has(nodeId) && !fromDone.has(nodeId)) return true
  }
  return false
}
